/**
 * Raport zarządu — teksty nowego układu zakładki „Koszty i należności” (`mbr.finance.ui.*`). WŁAŚCICIEL: agent sekcji
 * finance. Wpięte w `mbr-finance.ts` jako `ui`. `en` typowane względem `pl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrFinanceUiPl = {
  aiNote: 'Wniosek AI',
  costs: {
    trendTitle: 'Koszty — 12 miesięcy',
    trendSubtitle: 'Słupki skumulowane: stałe, zmienne, wynagrodzenia; linia: koszty razem jako % sprzedaży.',
    fixed: 'Stałe bez wynagrodzeń',
    variable: 'Zmienne bez wynagrodzeń',
    payroll: 'Wynagrodzenia',
    total: 'Koszty razem',
    costPct: 'Koszty, % sprzedaży',
    matrixTitle: 'Koszty wg kategorii i oddziału',
    matrixSubtitle:
      'Okres bieżący; % sprzedaży i zmiana względem okresu bazowego. Centrala = pozycje bez przypisania do oddziału.',
    category: 'Kategoria',
    company: 'Centrala',
    sum: 'Razem',
    pctOfSales: '% sprzedaży',
    change: 'Zmiana',
    group: '{amount} · {pct} sprzedaży',
    semiNote: 'Półzmienne (energia) liczą się do stałych — tak jak w progu rentowności.',
    aiCell: 'Wniosek AI dotyczy tej pozycji',
  },
  aging: {
    title: 'Wiekowanie należności',
    erp: 'Subiekt, stan na {date}',
    snapshots: 'Migawki: faktoring + pozostałe',
    open: 'Należności otwarte',
    current: 'Bieżące',
    upTo30: 'Do 30 dni po terminie',
    over30: 'Ponad 30 dni po terminie',
    overdue: 'Przeterminowane',
    overduePct: 'Udział przeterminowanych',
    cei: 'Skuteczność windykacji (CEI)',
    dso: 'Rotacja (DSO)',
    days: '{value} dni',
  },
  weekly: {
    title: 'Należności — trend tygodniowy',
    subtitle: 'Ostatnie tygodnie: należności otwarte i przeterminowane.',
    open: 'Otwarte',
    overdue: 'Przeterminowane',
  },
  blocks: {
    title: 'Faktoring i pozostałe',
    subtitle: 'Migawki działu finansów: stan, przeterminowane, zmiana w 7 dni i spłaty.',
    asOf: 'stan na {date}',
    noSnapshot: 'brak migawki w okresie',
    balance: 'Stan',
    overdue: 'Przeterminowane',
    overduePct: 'Udział przeterminowanych',
    change7d: 'Zmiana w 7 dni',
    repayments7d: 'Spłaty w 7 dni',
  },
  funnel: {
    title: 'Windykacja — etapy',
    subtitle: 'Sprawy w kancelarii na koniec okresu, w kolejności procesu.',
    cases: 'Sprawy',
    amount: 'Kwota',
    total: 'Razem w kancelarii',
    share: '{pct} kwoty',
  },
  hr: {
    title: 'Kadry wg działów',
    subtitle: 'Koszt pracodawcy i liczba osób w działach; dane osób tylko z uprawnieniem do kadr.',
    ratio: 'Na 1 zł sprzedaży: {value} zł',
    perHead: 'Koszt na osobę',
    perHour: 'Na godzinę',
    avgPerHead: 'Średnio na osobę: {value}',
  },
};

export const mbrFinanceUiEn: Shape<typeof mbrFinanceUiPl> = {
  aiNote: 'AI insight',
  costs: {
    trendTitle: 'Costs — 12 months',
    trendSubtitle: 'Stacked bars: fixed, variable, payroll; line: total costs as % of sales.',
    fixed: 'Fixed excl. payroll',
    variable: 'Variable excl. payroll',
    payroll: 'Payroll',
    total: 'Total costs',
    costPct: 'Costs, % of sales',
    matrixTitle: 'Costs by category and branch',
    matrixSubtitle:
      'Current period; % of sales and change vs the base period. HQ = items not assigned to a branch.',
    category: 'Category',
    company: 'HQ',
    sum: 'Total',
    pctOfSales: '% of sales',
    change: 'Change',
    group: '{amount} · {pct} of sales',
    semiNote: 'Semi-variable costs (energy) count as fixed — as in the break-even point.',
    aiCell: 'The AI insight is about this item',
  },
  aging: {
    title: 'Receivables ageing',
    erp: 'Subiekt, as of {date}',
    snapshots: 'Snapshots: factoring + other',
    open: 'Open receivables',
    current: 'Not due',
    upTo30: 'Up to 30 days overdue',
    over30: 'Over 30 days overdue',
    overdue: 'Overdue',
    overduePct: 'Overdue share',
    cei: 'Collection effectiveness (CEI)',
    dso: 'Turnover (DSO)',
    days: '{value} days',
  },
  weekly: {
    title: 'Receivables — weekly trend',
    subtitle: 'Recent weeks: open and overdue receivables.',
    open: 'Open',
    overdue: 'Overdue',
  },
  blocks: {
    title: 'Factoring and other',
    subtitle: 'Finance snapshots: balance, overdue, 7-day change and repayments.',
    asOf: 'as of {date}',
    noSnapshot: 'no snapshot in the period',
    balance: 'Balance',
    overdue: 'Overdue',
    overduePct: 'Overdue share',
    change7d: 'Change in 7 days',
    repayments7d: 'Repayments in 7 days',
  },
  funnel: {
    title: 'Collections — stages',
    subtitle: 'Cases at the law firm at the end of the period, in process order.',
    cases: 'Cases',
    amount: 'Amount',
    total: 'Total at the law firm',
    share: '{pct} of the amount',
  },
  hr: {
    title: 'HR by department',
    subtitle: 'Employer cost and headcount by department; per-person data only with the HR permission.',
    ratio: 'Per 1 PLN of sales: {value} PLN',
    perHead: 'Cost per person',
    perHour: 'Per hour',
    avgPerHead: 'Average per person: {value}',
  },
};
